/**
 * Module-based permissions for RBAC.
 * Canonical backend source: backend/constants/permissions.js
 * Effective resolution: frontend/src/utils/effectivePermissions.js (mirrors backend/utils/kpoPermissions.js)
 */

import { SHOW_MANUFACTURING_UI } from '../config/features';

const ALL_MODULES = {
    dashboard: 'Dashboard',
    distributionNetwork: 'Distribution Network',
    customers: 'Customers',
    products: 'Products',
    invoices: 'Invoices',
    drafts: 'Drafts',
    paymentsReceipts: 'Payments / Receipts',
    claims: 'Claims',
    bank: 'Bank',
    expenses: 'Expenses',
    payroll: 'Payroll',
    reports: 'Reports',
    users: 'Users',
    manufacturing: 'Manufacturing',
    tasks: 'Tasks',
    incentives: 'Incentives',
};

/** Modules shown in user creation / access panels (hides Manufacturing while MFG UI is off). */
export const MODULES = SHOW_MANUFACTURING_UI
    ? ALL_MODULES
    : Object.fromEntries(Object.entries(ALL_MODULES).filter(([key]) => key !== 'manufacturing'));

export const ACTIONS = ['view', 'create', 'edit', 'delete', 'approve'];

/** Labels used in the Access & Permissions UI (matches admin UX conventions). */
export const ACTION_LABELS = {
    view: 'View Only',
    create: 'Add',
    edit: 'Modify',
    delete: 'Delete',
    approve: 'Approve',
};

/** Modules that show the Approve column */
export const APPROVE_MODULES = ['invoices', 'drafts', 'paymentsReceipts', 'incentives', 'claims'];

/**
 * Receipts stay non-editable after create.
 * Invoices may be edited by KPO/admins (line items) when not GL-posted.
 */
export const NO_EDIT_MODULES = ['paymentsReceipts'];

/**
 * Which actions are applicable per module — the permissions UI only shows these.
 * Keeps the matrix honest: e.g. Dashboard is view-only, Receipts have no Modify.
 */
const ALL_MODULE_ACTIONS = {
    dashboard: ['view'],
    distributionNetwork: ['view', 'create', 'edit', 'delete'],
    customers: ['view', 'create', 'edit', 'delete'],
    products: ['view', 'create', 'edit', 'delete'],
    invoices: ['view', 'create', 'edit', 'delete', 'approve'],
    drafts: ['view', 'create', 'edit', 'delete', 'approve'],
    paymentsReceipts: ['view', 'create', 'delete', 'approve'],
    claims: ['view', 'create', 'edit', 'delete', 'approve'],
    bank: ['view', 'create', 'edit', 'delete'],
    expenses: ['view', 'create', 'edit', 'delete'],
    reports: ['view'],
    users: ['view', 'create', 'edit', 'delete'],
    manufacturing: ['view', 'create', 'edit', 'delete'],
    tasks: ['view', 'create', 'edit', 'delete'],
    incentives: ['view', 'create', 'edit', 'delete', 'approve'],
};

export const MODULE_ACTIONS = SHOW_MANUFACTURING_UI
    ? ALL_MODULE_ACTIONS
    : Object.fromEntries(Object.entries(ALL_MODULE_ACTIONS).filter(([key]) => key !== 'manufacturing'));

export function getApplicableActions(moduleKey) {
    return MODULE_ACTIONS[moduleKey] || ACTIONS.filter((action) => {
        if (action === 'approve') return APPROVE_MODULES.includes(moduleKey);
        if (action === 'edit') return !NO_EDIT_MODULES.includes(moduleKey);
        return true;
    });
}

/**
 * Default permission templates by role (Admin, Warehouse, Accountant, Salesman)
 */
export function getDefaultPermissionsByRole(role) {
    const adminModules = Object.keys(MODULES);
    const full = (overrides = {}) => ({
        view: true,
        create: true,
        edit: true,
        delete: true,
        approve: true,
        ...overrides,
    });
    const viewOnly = () => ({ view: true, create: false, edit: false, delete: false, approve: false });

    switch (role) {
        case 'CompanyAdmin':
        case 'Owner':
        case 'SaaSAdmin':
            return adminModules.reduce((acc, mod) => {
                acc[mod] = NO_EDIT_MODULES.includes(mod)
                    ? { view: true, create: true, edit: false, delete: true, approve: true }
                    : full();
                return acc;
            }, {});
        case 'SoleDistributorAdmin': {
            const perms = adminModules.reduce((acc, mod) => {
                acc[mod] = NO_EDIT_MODULES.includes(mod)
                    ? { view: true, create: true, edit: false, delete: true, approve: true }
                    : full();
                return acc;
            }, {});
            perms.manufacturing = { view: false, create: false, edit: false, delete: false, approve: false };
            // Distributors consume shared catalog — view + edit selling price only (no create/delete)
            perms.products = { view: true, create: false, edit: true, delete: false, approve: false };
            return perms;
        }
        case 'SubDistributorAdmin': {
            const perms = adminModules.reduce((acc, mod) => {
                acc[mod] = NO_EDIT_MODULES.includes(mod)
                    ? { view: true, create: true, edit: false, delete: true, approve: true }
                    : full();
                return acc;
            }, {});
            perms.distributionNetwork = { view: false, create: false, edit: false, delete: false, approve: false };
            perms.manufacturing = { view: false, create: false, edit: false, delete: false, approve: false };
            // View team targets / progress only — cannot assign targets or schemes
            perms.incentives = { view: true, create: false, edit: false, delete: false, approve: false };
            perms.products = { view: true, create: false, edit: true, delete: false, approve: false };
            return perms;
        }

        case 'WarehouseStaff':
            return {
                dashboard: viewOnly(),
                distributionNetwork: viewOnly(),
                customers: viewOnly(),
                products: full(),
                invoices: viewOnly(),
                drafts: viewOnly(),
                paymentsReceipts: viewOnly(),
                claims: viewOnly(),
                bank: viewOnly(),
                expenses: viewOnly(),
                payroll: { view: false, create: false, edit: false, delete: false, approve: false },
                reports: viewOnly(),
                users: { view: false, create: false, edit: false, delete: false, approve: false },
                manufacturing: viewOnly(),
                tasks: { view: true, create: false, edit: true, delete: false, approve: false },
                incentives: { view: false, create: false, edit: false, delete: false, approve: false },
            };

        case 'AccountsStaff':
        case 'Accountant': {
            const perms = adminModules.reduce((acc, mod) => {
                acc[mod] = NO_EDIT_MODULES.includes(mod)
                    ? { view: true, create: true, edit: false, delete: true, approve: true }
                    : full();
                return acc;
            }, {});
            // Accountants post invoices but only tenant admins may edit/delete posted documents
            perms.invoices = { view: true, create: true, edit: false, delete: false, approve: true };
            perms.users = { view: false, create: false, edit: false, delete: false, approve: false };
            perms.manufacturing = { view: false, create: false, edit: false, delete: false, approve: false };
            return perms;
        }

        case 'Salesman':
            return {
                dashboard: full(),
                distributionNetwork: viewOnly(),
                // Create-only: salesmen may add customers but never edit or delete.
                customers: { view: true, create: true, edit: false, delete: false, approve: false },
                products: viewOnly(),
                invoices: { view: true, create: true, edit: false, delete: false, approve: false },
                drafts: { view: true, create: true, edit: true, delete: true, approve: false },
                paymentsReceipts: { view: true, create: true, edit: false, delete: false, approve: false },
                claims: viewOnly(),
                bank: viewOnly(),
                expenses: viewOnly(),
                payroll: { view: false, create: false, edit: false, delete: false, approve: false },
                reports: { view: false, create: false, edit: false, delete: false, approve: false },
                users: { view: false, create: false, edit: false, delete: false, approve: false },
                manufacturing: { view: false, create: false, edit: false, delete: false, approve: false },
                tasks: { view: true, create: false, edit: true, delete: false, approve: false },
                incentives: viewOnly(),
            };

        case 'NSM':
            return {
                dashboard: full(),
                distributionNetwork: full(),
                customers: viewOnly(),
                products: viewOnly(),
                invoices: viewOnly(),
                drafts: viewOnly(),
                paymentsReceipts: viewOnly(),
                claims: { view: true, create: false, edit: false, delete: false, approve: true },
                bank: viewOnly(),
                expenses: viewOnly(),
                payroll: { view: false, create: false, edit: false, delete: false, approve: false },
                reports: full(),
                users: viewOnly(),
                manufacturing: viewOnly(),
                tasks: { view: true, create: true, edit: true, delete: false, approve: false },
                incentives: { view: true, create: true, edit: true, delete: false, approve: true },
            };

        case 'RSM':
        case 'ZSM':
            return {
                dashboard: full(),
                distributionNetwork: { view: true, create: false, edit: false, delete: false, approve: false },
                customers: viewOnly(),
                products: viewOnly(),
                invoices: viewOnly(),
                drafts: { view: true, create: false, edit: false, delete: false, approve: true },
                paymentsReceipts: viewOnly(),
                claims: { view: true, create: false, edit: false, delete: false, approve: true },
                bank: viewOnly(),
                expenses: viewOnly(),
                payroll: { view: false, create: false, edit: false, delete: false, approve: false },
                reports: full(),
                users: viewOnly(),
                manufacturing: { view: false, create: false, edit: false, delete: false, approve: false },
                tasks: { view: true, create: true, edit: true, delete: false, approve: false },
                incentives: { view: true, create: false, edit: false, delete: false, approve: true },
            };

        case 'ASM':
            return {
                dashboard: full(),
                distributionNetwork: viewOnly(),
                customers: full(),
                products: viewOnly(),
                invoices: { view: true, create: true, edit: false, delete: false, approve: false },
                drafts: { view: true, create: true, edit: true, delete: true, approve: true },
                paymentsReceipts: { view: true, create: true, edit: false, delete: false, approve: false },
                claims: viewOnly(),
                bank: viewOnly(),
                expenses: viewOnly(),
                payroll: { view: false, create: false, edit: false, delete: false, approve: false },
                reports: full(),
                users: viewOnly(),
                manufacturing: { view: false, create: false, edit: false, delete: false, approve: false },
                tasks: { view: true, create: true, edit: true, delete: false, approve: false },
                incentives: { view: true, create: false, edit: false, delete: false, approve: false },
            };

        case 'HRManager':
            return {
                dashboard: full(),
                distributionNetwork: viewOnly(),
                customers: viewOnly(),
                products: viewOnly(),
                invoices: viewOnly(),
                drafts: viewOnly(),
                paymentsReceipts: viewOnly(),
                claims: viewOnly(),
                bank: viewOnly(),
                expenses: viewOnly(),
                payroll: { view: true, create: false, edit: false, delete: false, approve: false },
                reports: { view: true, create: false, edit: false, delete: false, approve: false },
                users: full(),
                manufacturing: { view: false, create: false, edit: false, delete: false, approve: false },
                tasks: { view: true, create: true, edit: true, delete: false, approve: false },
                incentives: full(),
            };

        default:
            return adminModules.reduce((acc, mod) => {
                acc[mod] = mod === 'dashboard' ? viewOnly() : { view: false, create: false, edit: false, delete: false, approve: false };
                return acc;
            }, {});
    }
}

export const ROLE_TEMPLATES = [
    { value: 'Admin', label: 'Admin (full access)', role: 'CompanyAdmin' },
    { value: 'NSM', label: 'National Sales Manager', role: 'NSM' },
    { value: 'RSM', label: 'Regional Sales Manager', role: 'RSM' },
    { value: 'ZSM', label: 'Zone Sales Manager', role: 'ZSM' },
    { value: 'ASM', label: 'Area Sales Manager', role: 'ASM' },
    { value: 'KPO', label: 'KPO (inherits admin permissions except activity logs)', role: 'AccountsStaff' },
    { value: 'Warehouse', label: 'Warehouse (inventory, products)', role: 'WarehouseStaff' },
    { value: 'Accountant', label: 'Accountant (reports, receipts, payments)', role: 'Accountant' },
    { value: 'Salesman', label: 'Salesman (draft invoice & receipt workflow)', role: 'Salesman' },
    { value: 'HRManager', label: 'HR Manager', role: 'HRManager' },
];

/**
 * Empty permissions (all false)
 */
export function getEmptyPermissions() {
    return Object.keys(MODULES).reduce((acc, mod) => {
        acc[mod] = { view: false, create: false, edit: false, delete: false, approve: false };
        return acc;
    }, {});
}

/**
 * Normalize permissions from API (ensure all modules/actions exist; enforce no-edit for receipts)
 */
export function normalizePermissions(input) {
    const empty = getEmptyPermissions();
    if (!input || typeof input !== 'object') return { ...empty };
    const out = {};
    for (const mod of Object.keys(MODULES)) {
        out[mod] = { ...empty[mod], ...(input[mod] || {}) };
        if (NO_EDIT_MODULES.includes(mod)) out[mod].edit = false;
    }
    return out;
}
